import { Fragment, useCallback, useEffect, useState } from "react";
import {
  adminListRecordingFeedback,
  adminPatchRecordingFeedback,
  type AdminRecordingFeedback,
} from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { AdminPagination } from "./AdminPagination";

type StatusFilter = "new" | "treated" | "all";
type SortOrder = "newest" | "oldest";
const PAGE = 50;

const FILTER_OPTIONS: { key: StatusFilter; label: string }[] = [
  { key: "new", label: "Nieuw" },
  { key: "treated", label: "Behandeld" },
  { key: "all", label: "Alles" },
];

function truncate(text: string | null, max: number): string {
  if (!text) return "—";
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

export function AdminRecordingFeedbackTab() {
  const [items, setItems] = useState<AdminRecordingFeedback[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("new");
  const [sort, setSort] = useState<SortOrder>("newest");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminListRecordingFeedback({
        status: statusFilter === "all" ? undefined : statusFilter,
        offset,
        limit: PAGE,
        sort,
      });
      setItems(resp.feedback ?? []);
      setTotal(resp.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, offset, sort]);

  useEffect(() => {
    setOffset(0);
  }, [statusFilter, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleExpand = (item: AdminRecordingFeedback) => {
    if (expandedId === item.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(item.id);
    setReplyDraft(item.admin_reply ?? "");
  };

  const handleMarkTreated = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await adminPatchRecordingFeedback(id, {
        status: "treated",
        admin_reply: replyDraft.trim() || undefined,
      });
      setExpandedId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await adminPatchRecordingFeedback(id, { status: "new" });
      setExpandedId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1 rounded-full border border-[rgba(10,52,96,0.10)] bg-white p-1 shadow-[0_4px_12px_rgba(10,52,96,0.04)] w-fit">
          {FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setStatusFilter(opt.key)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                statusFilter === opt.key
                  ? "bg-[var(--cs-rose)] text-white shadow-[0_8px_18px_rgba(225,29,72,0.20)]"
                  : "text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value === "oldest" ? "oldest" : "newest")}
          className="rounded-lg border border-[rgba(10,52,96,0.18)] bg-white px-3 py-1.5 text-xs"
        >
          <option value="newest">Nieuwste eerst</option>
          <option value="oldest">Oudste eerst</option>
        </select>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Geen feedback gevonden.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                <th className="px-4 py-2">Datum</th>
                <th className="px-4 py-2">Gebruiker</th>
                <th className="px-4 py-2">Organisatie</th>
                <th className="px-4 py-2">Opname</th>
                <th className="px-4 py-2">Beoordeling</th>
                <th className="px-4 py-2">Opmerking</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {items.map((item) => (
                <Fragment key={item.id}>
                  <tr
                    onClick={() => toggleExpand(item)}
                    className="cursor-pointer text-xs hover:bg-[var(--cs-ivory)]"
                  >
                    <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                      {formatDateTime(item.created_at)}
                    </td>
                    <td className="px-4 py-2">
                      <p className="font-medium text-[var(--cs-navy)]">
                        {item.user_full_name || item.user_email || "—"}
                      </p>
                      {item.user_full_name && item.user_email ? (
                        <p className="text-[10px] text-[var(--cs-ink-muted)]">{item.user_email}</p>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-[var(--cs-navy)]">{item.organization_name || "—"}</td>
                    <td className="px-4 py-2 font-mono text-[11px] text-[var(--cs-navy)]">
                      {item.recording_label}
                    </td>
                    <td className="px-4 py-2 text-base">{item.rating === 1 ? "👍" : "👎"}</td>
                    <td className="max-w-[280px] truncate px-4 py-2 text-[var(--cs-navy)]">
                      {truncate(item.comment, 60)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          item.status === "treated"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {item.status === "treated" ? "Behandeld" : "Nieuw"}
                      </span>
                    </td>
                  </tr>
                  {expandedId === item.id ? (
                    <tr>
                      <td colSpan={7} className="bg-[var(--cs-ivory)]/80 px-4 py-3">
                        <div
                          className="space-y-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div>
                            <p className="mb-1 text-xs font-medium text-[var(--cs-navy)]">
                              Volledige opmerking
                            </p>
                            <p className="whitespace-pre-wrap text-xs text-[rgba(10,52,96,0.78)]">
                              {item.comment || "—"}
                            </p>
                          </div>

                          {item.recording_id ? (
                            <p className="text-[11px] text-[var(--cs-ink-muted)]">
                              Opname bestaat nog — zie tabblad Opnames.
                            </p>
                          ) : null}

                          <div>
                            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">
                              Reactie aan de gebruiker
                            </label>
                            <textarea
                              value={replyDraft}
                              onChange={(e) => setReplyDraft(e.target.value)}
                              maxLength={2000}
                              rows={3}
                              placeholder="Reactie aan de gebruiker (optioneel)"
                              className="w-full whitespace-pre-wrap rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-2 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
                            />
                          </div>

                          {item.status === "treated" ? (
                            <div className="flex items-center gap-3">
                              <p className="text-[11px] text-[var(--cs-ink-muted)]">
                                Behandeld op: {formatDateTime(item.treated_at)}
                              </p>
                              <button
                                type="button"
                                onClick={() => void handleReopen(item.id)}
                                disabled={busy}
                                className="rounded-lg border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1.5 text-xs font-medium text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
                              >
                                Heropen
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => void handleMarkTreated(item.id)}
                                disabled={busy}
                                className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                              >
                                {busy ? "Bezig…" : "Markeer als behandeld"}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdminPagination
        offset={offset}
        limit={PAGE}
        total={total}
        onPrev={() => setOffset((o) => Math.max(0, o - PAGE))}
        onNext={() => setOffset((o) => o + PAGE)}
      />
    </div>
  );
}
